import React, { useState } from 'react';
import { string } from 'prop-types';
import { RiMailSendFill } from 'react-icons/ri';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';

import WithAnimation from '@components/Common/WithAnimation';

import useMutation from '@/hooks/useMutation';
import useGuestName from '@/hooks/useGuestName';
import useSPH from '@hooks/useSPH';

import getTimeNow from '@invitato/helpers/dist/getTimeNow';

import { API_HOSTNAME, API_RSVP_TABLE } from '@/constants';
import { txtForm } from './locales';

const TYPE = {
  YES: 'YA',
  NO: 'TIDAK',
};

const DEFAULT_OPTION_PROPS = { style: { color: 'black' } };

const ERROR_TYPE = {
  name: undefined,
  email: undefined,
  phone: undefined,
  address: undefined,
};

const INPUT_COMMON_PROPS = {
  borderRadius: 'md',
  isRequired: true,
  size: 'md',
  type: 'text',
  variant: 'outline',
  colorScheme: 'blackAlpha',
  backgroundColor: 'theme.bgSecondary',
  color: 'secondaryColorText',
  _placeholder: { color: 'secondaryColorText' },
  _focus: { borderColor: 'bgAlternative' },
};

const SELECT_COMMON_PROPS = {
  size: 'md',
  variant: 'outline',
  backgroundColor: 'theme.bgSecondary',
  _focus: { borderColor: 'bgAlternative' },
  color: 'secondaryColorText',
};

const enc = (s) => encodeURIComponent(s);

/**
 * function to render RSVP component
 * @returns {JSX.Element}
 * @author idindrakusuma
 */
function RSVPSection({ lang, ...rest }) {
  const toast = useToast();
  const guestName = useGuestName();
  const sph = useSPH();
  const finalName = guestName.toLowerCase() === 'guest' ? '' : guestName;

  const [onSentConfirmation, loading] = useMutation(API_HOSTNAME, API_RSVP_TABLE, 'insert');

  const [name, setName] = useState(() => finalName);
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('-');
  const [attended, setAttended] = useState(TYPE.YES);
  const [adults, setAdults] = useState(1);
  const [childs, setChilds] = useState(1);
  const [errorType, setErrorType] = useState(ERROR_TYPE);

  /**
   * function to set state
   * @param {object} e - html event value
   * @param {function} setState
   * @param {string} typeState
   * @returns
   */
  const onSetState = (e, setState, typeState) => {
    const value = e.target.value;
    setState(value);

    if (typeState === 'email') {
      if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
        setErrorType({ ...errorType, email: txtForm.invalidEmail[lang] });
        return;
      }
    }

    if (typeState === 'phone') {
      if (!/\d+/.test(value) || value.length > 13) {
        setErrorType({ ...errorType, phone: txtForm.invalidPhone[lang] });
        return;
      }
    }

    setErrorType(ERROR_TYPE);
  };

  /**
   * function to submit to BE with check the form value first
   */
  const onSubmitForm = async () => {
    if (errorType.name || errorType.phone || errorType.address) return;

    if (!name || !phone || !address) {
      setErrorType({
        name: !name ? txtForm.required[lang] : '',
        phone: !phone ? txtForm.required[lang] : '',
        address: !address ? txtForm.required[lang] : '',
      });

      return;
    }

    const query =
      `nama=${enc(name)}` +
      `&hp=${phone}` +
      `&hadir=${attended}` +
      `&alamat=${enc(address)}` +
      `&adults=${attended ? adults : 0}` +
      `&childs=${attended ? childs : 0}` +
      `&date=${getTimeNow()}` +
      `&type=${encodeURIComponent(sph.desc)}`;

    const result = await onSentConfirmation(query);

    if (result.success) {
      toast({
        title: txtForm.success[lang],
        description: txtForm.msgSuccess[lang],
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setName('');
      setPhone('');
      setAddress('');
      setAdults(1);
      setChilds(1);
    } else {
      toast({
        title: 'Oops!',
        description: txtForm.msgError[lang],
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minHeight="300px" paddingBottom="32px" {...rest}>
      <WithAnimation>
        <Text align="center" color="white" fontSize="md">
          Please confirm your attendance through the form below (1 registration for 1 family)
        </Text>
      </WithAnimation>
      <WithAnimation>
        <Box
          backgroundColor="secondaryColorText"
          borderRadius="2xl"
          boxShadow="xl"
          marginTop="24px"
          padding="24px"
        >
          <FormControl margin="8px 0" isInvalid={errorType.name}>
            <FormLabel fontSize="md" color="bgAlternative" margin="8px 0">
              {`Parent's Name`}
            </FormLabel>
            <Input
              {...INPUT_COMMON_PROPS}
              placeholder="..."
              value={name}
              onChange={(e) => onSetState(e, setName)}
            />
            <FormErrorMessage marginTop="4px">{errorType.name}</FormErrorMessage>
          </FormControl>

          <FormControl margin="8px 0" isInvalid={errorType.phone}>
            <FormLabel fontSize="md" color="bgAlternative" margin="8px 0">
              Phone Number (e.g.: 0812345678xx)
            </FormLabel>
            <Input
              {...INPUT_COMMON_PROPS}
              placeholder="..."
              value={phone}
              onChange={(e) => onSetState(e, setPhone, 'phone')}
            />
            <FormErrorMessage marginTop="4px">{errorType.phone}</FormErrorMessage>
          </FormControl>

          {false && (
            <FormControl margin="8px 0" isInvalid={errorType.address}>
              <Input
                {...INPUT_COMMON_PROPS}
                height="40px"
                placeholder={txtForm.address[lang]}
                value={address}
                onChange={(e) => onSetState(e, setAddress)}
              />
              <FormErrorMessage marginTop="4px">{errorType.address}</FormErrorMessage>
            </FormControl>
          )}

          <FormControl margin="8px 0">
            <FormLabel fontSize="md" color="bgAlternative" margin="8px 0">
              Will you be attending the Event?
            </FormLabel>
            <Select
              {...SELECT_COMMON_PROPS}
              value={attended}
              onChange={(e) => onSetState(e, setAttended)}
            >
              <option value={TYPE.YES} color="white" {...DEFAULT_OPTION_PROPS}>
                {txtForm.willAttend[lang]}
              </option>
              <option value={TYPE.NO} {...DEFAULT_OPTION_PROPS}>
                {`No, I won't be attending`}
              </option>
            </Select>
          </FormControl>
          {attended === TYPE.YES && (
            <>
              <FormControl>
                <FormLabel fontSize="md" color="bgAlternative" margin="8px 0">
                  How many adults will be attending?
                </FormLabel>
                <Select
                  {...SELECT_COMMON_PROPS}
                  value={adults}
                  onChange={(e) => onSetState(e, setAdults)}
                >
                  <option value={1} {...DEFAULT_OPTION_PROPS}>
                    1
                  </option>
                  <option value={2} {...DEFAULT_OPTION_PROPS}>
                    2
                  </option>
                  <option value={3} {...DEFAULT_OPTION_PROPS}>
                    3
                  </option>
                  <option value={4} {...DEFAULT_OPTION_PROPS}>
                    4
                  </option>
                  <option value={5} {...DEFAULT_OPTION_PROPS}>
                    5
                  </option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="md" color="bgAlternative" margin="8px 0">
                  {`How many children will be attending?`} <br /> {`(<10 years old)`}
                </FormLabel>
                <Select
                  {...SELECT_COMMON_PROPS}
                  value={childs}
                  onChange={(e) => onSetState(e, setChilds)}
                >
                  {sph.maxChild > 0 && (
                    <option value={1} {...DEFAULT_OPTION_PROPS}>
                      1
                    </option>
                  )}
                  {sph.maxChild > 1 && (
                    <option value={2} {...DEFAULT_OPTION_PROPS}>
                      2
                    </option>
                  )}
                  {sph.maxChild > 2 && (
                    <option value={3} {...DEFAULT_OPTION_PROPS}>
                      3
                    </option>
                  )}
                  {sph.maxChild > 3 && (
                    <option value={4} {...DEFAULT_OPTION_PROPS}>
                      4
                    </option>
                  )}
                  {sph.maxChild > 4 && (
                    <option value={5} {...DEFAULT_OPTION_PROPS}>
                      5
                    </option>
                  )}
                </Select>
              </FormControl>
            </>
          )}
          <Box display="flex" justifyContent="end">
            <Button
              color="white"
              colorScheme="blackAlpha"
              fontWeight="medium"
              isLoading={loading}
              leftIcon={<RiMailSendFill />}
              marginTop="16px"
              size="md"
              type="button"
              bgColor="mainColorText"
              onClick={onSubmitForm}
            >
              {txtForm.submit[lang]}
            </Button>
          </Box>
        </Box>
      </WithAnimation>
    </Box>
  );
}

RSVPSection.propTypes = {
  lang: string,
};

RSVPSection.defaultProps = {
  lang: 'id',
};

export default React.memo(RSVPSection);
